import type { Router } from "vue-router"
import { captureError, captureWarning } from "@/sentry"

// A deploy replaces the content-hashed asset files. A page that has been open across one — or that reused a
// cached index.html — asks for a chunk that no longer exists, and the lazy-loaded view never renders. A phone keeps
// the tab (or the home-screen app) for weeks, so reloading once is what gets it onto the current build at all.
// Taken from Backoffice, which met this first.
const RELOAD_KEY = "stale-asset-reload-at"
// Long enough to cover the reload itself, short enough that a later deploy can recover again
const RELOAD_COOLDOWN_MS = 60_000

function readReloadedAt(): number {
  try {
    const stored = Number(sessionStorage.getItem(RELOAD_KEY))
    return Number.isFinite(stored) ? stored : 0
  } catch {
    return 0
  }
}

function reloadedRecently(): boolean {
  const at = readReloadedAt()
  return at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS
}

function isStaleAssetError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  // Chrome, Safari and Firefox each word this differently
  return /dynamically imported module|Importing a module script failed/i.test(message)
}

function recover(error: unknown, phase: "router" | "preload"): void {
  // Failing again right after a reload means the assets really are gone, not just outdated
  if (reloadedRecently()) {
    captureError("Asset load failed again after reload", { extra: { phase, message: String(error) } })
    return
  }

  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // Without the guard a reload loop is possible, so leave the page alone instead
    captureError("Asset load failed and the reload guard is unavailable", { extra: { phase, message: String(error) } })
    return
  }

  window.location.reload()
}

// Reported after the reload rather than before it, so the event is sent from a live page — and only once
// the session is restored, so Sentry knows which user and company was stuck on an outdated build.
export function reportStaleAssetRecovery(): void {
  if (!reloadedRecently()) return
  captureWarning("Recovered from a stale asset after reload", { extra: { url: window.location.href } })
}

export function installStaleAssetRecovery(router: Router): void {
  router.onError(error => {
    if (isStaleAssetError(error)) recover(error, "router")
  })

  // Vite fires this when a preloaded chunk fails; preventDefault stops the unhandled rejection
  window.addEventListener("vite:preloadError", event => {
    event.preventDefault()
    recover((event as Event & { payload?: unknown }).payload, "preload")
  })
}
